import { type ObjectMetadata, Stack, Text, TextLink } from '@olytic/foundation';
import { Link } from 'react-router-dom';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { recordHref } from '../../objects/routes';
import { asCheck, asDateTime, asLabel, column, createdByField, field, lastModifiedByField, lookup, type Row, related, text } from './kit';

const def = OBJECT_DEFS.guidelines;

interface ProposalDeal { opportunity_id?: string; name?: string; outcome?: string; why?: string }

/** The closed deals a playbook proposal rests on, each linked, with why. */
function asDeals(value: unknown) {
  const deals = Array.isArray(value) ? (value as ProposalDeal[]) : [];
  if (deals.length === 0) return null;
  return (
    <Stack gap="xs">
      {deals.map((deal, i) => {
        const href = deal.opportunity_id ? recordHref('opportunities', deal.opportunity_id) : null;
        const name = deal.name ?? 'Opportunity';
        return (
          <Text key={i} size="sm">
            {href ? <TextLink asChild><Link to={href}>{name}</Link></TextLink> : name}
            {deal.outcome ? ` (${deal.outcome === 'closed_won' ? 'won' : 'lost'})` : ''}
            {deal.why ? `: ${deal.why}` : ''}
          </Text>
        );
      })}
    </Stack>
  );
}

/** One statement of how Olytic prices or delivers. Rationale: guidelines.md. */
export const guidelines: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Guideline',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('name', 'Name'),
    field('topic', 'Topic', { format: asLabel }),
    field('scope', 'Scope', { format: asLabel }),
    field('enforcement', 'Enforcement', { format: asLabel }),
    field('weight', 'Weight'),
    field('active', 'Active', { format: asCheck }),
    field('opportunity_name', 'Opportunity', { link: lookup('opportunities', 'opportunity_id') }),
    field('statement', 'Statement', { wide: true }),
    field('rule_type', 'Rule', { format: asLabel }),
    field('rule_value', 'Rule Value'),
    field('source', 'Drafted by', { format: asLabel }),
    field('source_ref', 'Source', { wide: true }),
    field('review_status', 'Review', { format: asLabel }),
    field('evidence', 'Deals Behind It', { format: asDeals, wide: true }),
    field('created_at', 'Created', { format: asDateTime }),
    field('updated_at', 'Last Modified', { format: asDateTime }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['name', 'topic', 'scope', 'enforcement', 'weight', 'active', 'opportunity_name'] },
    { key: 'statement', label: 'Statement', fields: ['statement'] },
    { key: 'rule', label: 'Rule the AI Checks', fields: ['rule_type', 'rule_value'] },
    { key: 'proposal', label: 'Proposed by the Playbook', collapsed: true, fields: ['review_status', 'source_ref', 'evidence'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['source', 'created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  related: [
    related('products', 'Products', 'products', [
      column('name', 'Product', { primary: true }),
      column('status', 'Status', { format: asLabel }),
    ]),
    related('deliverable-types', 'Deliverable Types', 'deliverable-types', [
      column('name', 'Deliverable Type', { primary: true }),
      column('code', 'Code'),
    ]),
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'topic', 'scope'],
};
